import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, RotateCcw, AlertCircle, Loader2, CalendarClock, CreditCard, Trash2, PauseCircle } from 'lucide-react';
import { Button, Card, CardContent } from '@/components/ui';
import { useCustomerPortal, type BillingInfo } from '@/hooks/useSubscription';
import { useReactivateSubscription } from '@/hooks/useReactivateSubscription';
import {
  blockReason,
  daysUntil,
  formatBillingDate,
  graceEndPassed,
  graceEndsAt,
  nonRenewingPlanEnd,
} from '@/lib/billingAccess';
import { billingErrorText } from './billingMessages';
import { PlanChangeFlow, type PlanChangeRequest } from './PlanChangeFlow';

/**
 * Banner above dashboard content describing where the host's plan stands.
 * One of (data-status):
 *
 *   deletion_pending — account deletion requested: date + link to undo
 *   suspended        — grace over: guest portals off, data kept, pay
 *   grace            — plan ended: until X portals work, nothing new; pay
 *   no_plan          — never paid / trial over
 *   past_due         — card failed, Stripe retries: update card (portal)
 *   cancel_scheduled — renewal off, access until X: turn renewal back on
 *   expiring_soon    — gift / beta / trial (no auto-renew) ending ≤ 14 days
 *   (nothing)        — admins, auto-renewing payers, plans far from ending
 *
 * Dates in the past are never announced as "today".
 */
export type LifecycleVariant =
  | 'deletion_pending'
  | 'suspended'
  | 'grace'
  | 'no_plan'
  | 'past_due'
  | 'cancel_scheduled'
  | 'expiring_soon';

interface LifecycleBannerProps {
  billingInfo: BillingInfo | null | undefined;
  /** Variants this page shows in its own way (e.g. Billing's blocked card). */
  hide?: LifecycleVariant[];
}

export function lifecycleVariant(info: BillingInfo | null | undefined, now: Date = new Date()): LifecycleVariant | null {
  if (!info || info.role === 'admin') return null;
  const reason = blockReason(info, now);
  if (reason === 'deletion_pending') return 'deletion_pending';
  if (reason === 'suspended') return 'suspended';
  if (reason === 'grace') return 'grace';
  if (reason === 'no_plan') return 'no_plan';
  const status = String(info.subscription_status);
  if (status === 'past_due') return 'past_due';
  if (status === 'cancel_scheduled') return 'cancel_scheduled';
  const end = nonRenewingPlanEnd(info);
  const days = daysUntil(end, now);
  if (days !== null && days >= 0 && days <= 14) return 'expiring_soon';
  return null;
}

const TONES = {
  red: {
    bg: 'border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800',
    icon: 'text-red-600 dark:text-red-400',
    head: 'text-red-900 dark:text-red-100',
    body: 'text-red-800 dark:text-red-200',
  },
  orange: {
    bg: 'border-orange-200 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800',
    icon: 'text-orange-600 dark:text-orange-400',
    head: 'text-orange-900 dark:text-orange-100',
    body: 'text-orange-800 dark:text-orange-200',
  },
  yellow: {
    bg: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-800',
    icon: 'text-yellow-600 dark:text-yellow-400',
    head: 'text-yellow-900 dark:text-yellow-100',
    body: 'text-yellow-800 dark:text-yellow-200',
  },
} as const;

export function LifecycleBanner({ billingInfo, hide = [] }: LifecycleBannerProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'hr' ? 'hr' : i18n.language;
  const reactivate = useReactivateSubscription();
  const portal = useCustomerPortal();
  const [error, setError] = useState<string | null>(null);
  // Stripe key mix-up: retrying can't help — the text says to contact support.
  const [unavailable, setUnavailable] = useState(false);
  const [checkoutRequest, setCheckoutRequest] = useState<PlanChangeRequest | null>(null);

  const variant = lifecycleVariant(billingInfo);
  if (!billingInfo || !variant || hide.includes(variant)) return null;

  const fmt = (iso: string | null | undefined) => formatBillingDate(iso, lang);

  const handleReactivate = async () => {
    setError(null);
    try {
      const result = await reactivate.mutateAsync();
      // The subscription already ended → confirm the price, then Stripe.
      if (result.needsCheckout && result.checkoutRequest) setCheckoutRequest(result.checkoutRequest);
    } catch (e) {
      const code = (e as { code?: string })?.code;
      if (code === 'BILLING_UNAVAILABLE') setUnavailable(true);
      setError(billingErrorText(t, code));
    }
  };

  const handlePortal = async () => {
    setError(null);
    try {
      const result = await portal.mutateAsync();
      window.location.href = result.url;
    } catch (e) {
      setError(billingErrorText(t, (e as { code?: string })?.code));
    }
  };

  let tone: (typeof TONES)[keyof typeof TONES] = TONES.yellow;
  let Icon = Clock;
  let title = '';
  let body = '';
  let action: React.ReactNode = null;

  const plansLink = (
    <Link to="/dashboard/billing">
      <Button className="gap-2 whitespace-nowrap">
        <CreditCard className="h-4 w-4" />
        {t('billing.lifecycle.choosePlan', 'Odaberi plan')}
      </Button>
    </Link>
  );

  switch (variant) {
    case 'deletion_pending': {
      tone = TONES.orange;
      Icon = Trash2;
      const date = fmt(billingInfo.deletion_scheduled_for);
      title = t('billing.lifecycle.deletionTitle', 'Brisanje računa je zakazano');
      body = date
        ? t('billing.lifecycle.deletionBody', {
            date,
            defaultValue: 'Račun i svi podaci bit će trajno obrisani {{date}}. Do tada brisanje možeš poništiti.',
          })
        : t('billing.lifecycle.deletionBodyNoDate', 'Račun i svi podaci uskoro će biti trajno obrisani. Do tada brisanje možeš poništiti.');
      action = (
        <Link to="/dashboard/account">
          <Button className="gap-2 whitespace-nowrap">
            <RotateCcw className="h-4 w-4" />
            {t('account.undoDelete', 'Poništi brisanje')}
          </Button>
        </Link>
      );
      break;
    }
    case 'suspended':
      tone = TONES.red;
      Icon = PauseCircle;
      title = t('billing.lifecycle.suspendedTitle', 'Račun je suspendiran');
      body = t('billing.lifecycle.suspendedBody', 'Gosti više ne vide svoje portale. Svi tvoji podaci su sačuvani — čim platiš plan, sve se odmah vraća.');
      action = plansLink;
      break;
    case 'grace': {
      tone = TONES.orange;
      Icon = AlertCircle;
      const date = fmt(graceEndsAt(billingInfo));
      title = t('billing.lifecycle.graceTitle', 'Plan je istekao');
      body = graceEndPassed(billingInfo)
        ? t('billing.lifecycle.graceBodyTonight', 'Plan je istekao i 30 dana je prošlo — portali gostiju zatvaraju se tijekom noći. Odaberi plan da ostanu otvoreni; podaci ostaju sačuvani.')
        : date
        ? t('billing.lifecycle.graceBody', {
            date,
            defaultValue: 'Do {{date}} gosti i dalje vide svoje portale, a ti vidiš i uređuješ sve podatke — ali ne možeš dodavati nove objekte, jedinice ni rezervacije. Nakon tog datuma portali gostiju se gase. Podaci ostaju sačuvani.',
          })
        : t('billing.lifecycle.graceBodyNoDate', 'Gosti još vide svoje portale, a ti vidiš i uređuješ sve podatke — ali ne možeš dodavati nove objekte, jedinice ni rezervacije. Odaberi plan prije nego se portali ugase.');
      action = plansLink;
      break;
    }
    case 'no_plan':
      tone = TONES.yellow;
      Icon = CreditCard;
      title = t('billing.lifecycle.noPlanTitle', 'Nemaš aktivan plan');
      body = t('billing.lifecycle.noPlanBody', 'Odaberi plan da možeš dodati objekte, jedinice i rezervacije i poslati gostima njihov portal.');
      action = plansLink;
      break;
    case 'past_due':
      tone = TONES.red;
      Icon = AlertCircle;
      title = t('billing.lifecycle.pastDueTitle', 'Naplata kartice nije uspjela');
      body = t('billing.lifecycle.pastDueBody', 'Stripe će naplatu pokušati ponovno. Sve i dalje radi — ažuriraj karticu da pretplata ne istekne.');
      action = (
        <Button onClick={handlePortal} disabled={portal.isPending} className="gap-2 whitespace-nowrap">
          {portal.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {t('billing.lifecycle.updateCard', 'Ažuriraj karticu')}
        </Button>
      );
      break;
    case 'cancel_scheduled': {
      tone = TONES.yellow;
      Icon = Clock;
      const date = fmt(billingInfo.current_period_end);
      title = t('billing.lifecycle.cancelScheduledTitle', 'Automatska obnova je isključena');
      body = date
        ? t('billing.lifecycle.cancelScheduledBody', {
            date,
            defaultValue: 'Plan vrijedi do {{date}} i neće se obnoviti. Obnovu možeš ponovno uključiti — ništa se ne naplaćuje do tog datuma.',
          })
        : t('billing.lifecycle.cancelScheduledBodyNoDate', 'Plan se neće obnoviti. Obnovu možeš ponovno uključiti jednim klikom.');
      action = (
        <Button onClick={handleReactivate} disabled={reactivate.isPending || unavailable} className="gap-2 whitespace-nowrap">
          {reactivate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
          {t('billing.lifecycle.resume', 'Uključi obnovu')}
        </Button>
      );
      break;
    }
    case 'expiring_soon': {
      const end = nonRenewingPlanEnd(billingInfo);
      const days = daysUntil(end) ?? 0;
      tone = days <= 3 ? TONES.red : days <= 7 ? TONES.orange : TONES.yellow;
      Icon = CalendarClock;
      const isTrial = billingInfo.plan_source === 'trial' || (!billingInfo.plan_source && billingInfo.is_trialing);
      title = isTrial
        ? days === 0
          ? t('billing.lifecycle.trialEndsToday', 'Probno razdoblje završava danas')
          : days === 1
            ? t('billing.lifecycle.trialEndsTomorrow', 'Probno razdoblje završava sutra')
            : t('billing.lifecycle.trialEndsIn', { days, defaultValue: 'Probno razdoblje završava za {{days}} dana' })
        : days === 0
          ? t('billing.lifecycle.planEndsToday', 'Plan završava danas')
          : days === 1
            ? t('billing.lifecycle.planEndsTomorrow', 'Plan završava sutra')
            : t('billing.lifecycle.planEndsIn', { days, defaultValue: 'Plan završava za {{days}} dana' });
      body = t('billing.lifecycle.planEndsBody', {
        date: fmt(end),
        defaultValue: 'Tvoj plan vrijedi do {{date}} i ne obnavlja se sam. Odaberi plan da nastaviš bez prekida.',
      });
      action = plansLink;
      break;
    }
  }

  return (
    <Card data-testid="lifecycle-banner" data-status={variant} className={tone.bg}>
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <Icon className={`h-5 w-5 shrink-0 ${tone.icon}`} />
        <div className="flex-1">
          <p className={`font-medium ${tone.head}`}>{title}</p>
          <p className={`text-sm ${tone.body}`}>{body}</p>
        </div>
        {action}
      </CardContent>
      {error && (
        <CardContent className="pt-0 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </CardContent>
      )}
      {checkoutRequest && (
        <PlanChangeFlow request={checkoutRequest} billing={billingInfo} returnTo="billing" onClose={() => setCheckoutRequest(null)} />
      )}
    </Card>
  );
}

export default LifecycleBanner;
